/**
 * plan-facts.ts — pure marketing-copy adapter over the Stripe plan source of truth.
 *
 * ROOT-CAUSE/CLASS: landing copy hand-typed prices/limits/bot terms and drifted from
 * SUBSCRIPTION_PLANS/MODULE_CATALOG — content.ts's pricing-copy-sync.test.ts exists
 * because of the resulting incident (2026-06-11, "200 leads" shown while the app
 * enforced 500). That test cross-checks values AFTER the fact; this adapter removes
 * the retyping in the first place — content.ts reads these facts instead of literals.
 *
 * Cross-feature import: marketing -> subscriptions, allowlisted in
 * .arch-allow/cross-feature.json (ARCH-MARKETING-SUBSCRIPTIONS) — PlanFitQuiz.tsx was
 * the original consumer of that entry and has since been deleted; this adapter plus
 * content.ts's direct read are the surviving consumers of the same allowlisted edge.
 *
 * Pure formatting only — no React, no 'use client'. Safe for content.ts (a plain data
 * module evaluated at import time, consumed by both server and client components) to
 * import directly.
 */
import { SUBSCRIPTION_PLANS, MODULE_CATALOG } from '@/features/subscriptions/lib/stripe/plans';
import { formatPrice } from '@/lib/utils/format-price';

const basic = SUBSCRIPTION_PLANS.basic;
const pro = SUBSCRIPTION_PLANS.pro;
const bot = MODULE_CATALOG.ai_lead_onboarding;

/** '39.99' -> '€39.99' — routes through the canonical formatPrice() helper so the
 * symbol derives from the plan's own currency field, never a hardcoded glyph
 * (2026-06-04 FitActive incident: a hardcoded € showed the wrong symbol to RON orgs). */
function formatPlanPrice(amount: number, currency: string): string {
  return formatPrice(amount, currency.toUpperCase());
}

/**
 * formatVatLabel — R3 judge-E-verdict.md Decision 7 derivation contract. The
 * landing's "TVA inclus"/"VAT included" label is a PURE function of the plan's
 * own `priceIncludesVat` source boolean (plans.ts) — content.ts assigns it from
 * PLAN_FACTS.<tier>.vatLabel, PricingSection.tsx renders `t(tier.vatLabel)`, and
 * no VAT literal or percentage lives in either place. Only the true path is
 * designed: both offered plans currently ship `priceIncludesVat: true`, and
 * there is no "excluding VAT" copy anywhere left to fall back to. If a plan's
 * source boolean is ever false, this throws rather than inventing untested
 * copy — "stop rather than hardcode" (verdict Decision 7).
 */
export function formatVatLabel(includesVat: boolean): { en: string; ro: string } {
  if (!includesVat) {
    throw new Error(
      'formatVatLabel(false): no VAT-excluded landing copy is designed — plans.ts priceIncludesVat must stay true for offered plans, or this function needs a new decision, not a hardcoded fallback.'
    );
  }
  return { en: 'VAT included', ro: 'TVA inclus' };
}

export const PLAN_FACTS = {
  starter: {
    priceLabel: formatPlanPrice(basic.price, basic.currency), // '€39.99'
    leads: basic.limits.leads, // 500
    users: basic.limits.users, // 3
    vatLabel: formatVatLabel(basic.priceIncludesVat),
  },
  pro: {
    priceLabel: formatPlanPrice(pro.price, pro.currency), // '€79.99'
    leads: pro.limits.leads, // Infinity
    users: pro.limits.users, // 15
    vatLabel: formatVatLabel(pro.priceIncludesVat),
  },
  bot: {
    monthlyPrice: bot.price, // 50
    trialDays: bot.trialDays, // 7
    includedConversations: bot.quota.includedConversations, // 200
    overagePrice: bot.quota.overagePrice, // 5
    overageBlockSize: bot.quota.overageBlockSize, // 50
  },
} as const;

/**
 * A plan limit as a bilingual display pair. `Infinity` (unlimited leads on Pro)
 * renders as the word, never the literal value — every limit render in content.ts
 * goes through this so "Infinity" can never leak into rendered copy.
 */
export function formatLimit(value: number): { en: string; ro: string } {
  if (value === Infinity) return { en: 'Unlimited', ro: 'Nelimitat' };
  return { en: String(value), ro: String(value) };
}
